import { produce } from "immer";
import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

export type CacheUpdater<T> = Partial<T> | ((draft: T) => void);
export type Identifier<T> = (a: T, b: T) => boolean;

export const applyDraftUpdate = <T extends object>(
  target: T,
  updater: CacheUpdater<T>
): void => {
  if (typeof updater === "function") {
    updater(target);
  } else {
    Object.assign(target, updater);
  }
};

const createBaseOps = <TData>(
  queryClient: QueryClient,
  queryKey: QueryKey
) => ({
  get: () => queryClient.getQueryData<TData>(queryKey),
  replace: (data: TData) => queryClient.setQueryData<TData>(queryKey, data),
  invalidate: () => queryClient.invalidateQueries({ queryKey }),
  reset: () => queryClient.resetQueries({ queryKey }),
  remove: () => queryClient.removeQueries({ queryKey }),
});

interface RecordOps<TData extends object> {
  get: () => TData | undefined;
  replace: (data: TData) => void;
  invalidate: () => void;
  reset: () => void;
  remove: () => void;
  patch: (updater: CacheUpdater<TData>) => void;
  patchField: <K extends keyof TData>(
    key: K,
    updater: CacheUpdater<TData[K]>
  ) => void;
}

export const createRecordOps = <TData extends object>(
  queryClient: QueryClient,
  queryKey: QueryKey
): RecordOps<TData> => ({
  ...createBaseOps<TData>(queryClient, queryKey),

  patch: (updater) => {
    queryClient.setQueryData<TData>(queryKey, (old) => {
      if (!old) return old;
      return produce(old, (draft: TData) => {
        applyDraftUpdate(draft, updater);
      });
    });
  },

  patchField: (key, updater) => {
    queryClient.setQueryData<TData>(queryKey, (old) => {
      if (!old) return old;
      return produce(old, (draft: TData) => {
        applyDraftUpdate(draft[key] as object, updater);
      });
    });
  },
});

interface ListOps<T> {
  get: () => T[] | undefined;
  replace: (data: T[]) => void;
  invalidate: () => void;
  reset: () => void;
  remove: () => void;
  add: (item: T) => void;
  addOrUpdate: (item: T) => void;
  patch: (item: T, updater: CacheUpdater<T>) => void;
  delete: (item: T) => void;
}

export const createListOps = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  identify: Identifier<T>
): ListOps<T> => {
  const findIndex = (list: T[], item: T) =>
    list.findIndex((i) => identify(i, item));

  return {
    ...createBaseOps<T[]>(queryClient, queryKey),

    add: (item) => {
      queryClient.setQueryData<T[]>(queryKey, (old = []) =>
        produce(old, (draft) => {
          const list = draft as T[];
          list.unshift(item);
        })
      );
    },

    addOrUpdate: (item) => {
      queryClient.setQueryData<T[]>(queryKey, (old = []) =>
        produce(old, (draft) => {
          const list = draft as T[];
          const idx = findIndex(list, item);
          if (idx >= 0) list[idx] = item;
          else list.unshift(item);
        })
      );
    },

    patch: (item, updater) => {
      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const list = draft as T[];
          const idx = findIndex(list, item);
          if (idx >= 0) applyDraftUpdate(list[idx] as object, updater);
        });
      });
    },

    delete: (item) => {
      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const list = draft as T[];
          const idx = findIndex(list, item);
          if (idx >= 0) list.splice(idx, 1);
        });
      });
    },
  };
};

interface PaginatedResponse<T> {
  items: T;
  total: number;
}

interface PaginatedListOps<T> {
  get: () => PaginatedResponse<T[]> | undefined;
  replace: (data: PaginatedResponse<T[]>) => void;
  invalidate: () => void;
  reset: () => void;
  remove: () => void;
  addOrUpdate: (item: T) => void;
  patch: (item: T, updater: CacheUpdater<T>) => void;
  delete: (item: T) => void;
}

export const createPaginatedListOps = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  identify: Identifier<T>
): PaginatedListOps<T> => {
  const findIndex = (list: T[], item: T) =>
    list.findIndex((i) => identify(i, item));

  return {
    ...createBaseOps<PaginatedResponse<T[]>>(queryClient, queryKey),

    addOrUpdate: (item) => {
      queryClient.setQueryData<PaginatedResponse<T[]>>(queryKey, (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const items = draft.items as T[];
          const idx = findIndex(items, item);
          if (idx >= 0) items[idx] = item;
          else items.unshift(item);
        });
      });
    },

    patch: (item, updater) => {
      queryClient.setQueryData<PaginatedResponse<T[]>>(queryKey, (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const items = draft.items as T[];
          const idx = findIndex(items, item);
          if (idx >= 0) applyDraftUpdate(items[idx] as object, updater);
        });
      });
    },

    delete: (item) => {
      queryClient.setQueryData<PaginatedResponse<T[]>>(queryKey, (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const items = draft.items as T[];
          const idx = findIndex(items, item);
          if (idx >= 0) items.splice(idx, 1);
        });
      });
    },
  };
};

const findInPages = <T>(
  pages: T[][],
  item: T,
  identify: Identifier<T>
): [number, number] | null => {
  for (let page = 0; page < pages.length; page++) {
    const idx = pages[page].findIndex((i) => identify(i, item));
    if (idx >= 0) return [page, idx];
  }
  return null;
};

const findInPaginatedPages = <T>(
  pages: PaginatedResponse<T[]>[],
  item: T,
  identify: Identifier<T>
): [number, number] | null => {
  for (let page = 0; page < pages.length; page++) {
    const idx = pages[page].items.findIndex((i) => identify(i, item));
    if (idx >= 0) return [page, idx];
  }
  return null;
};

interface InfiniteListOps<T> {
  get: () => InfiniteData<T[]> | undefined;
  replace: (data: InfiniteData<T[]>) => void;
  invalidate: () => void;
  reset: () => void;
  remove: () => void;
  addOrUpdate: (item: T) => void;
  patch: (item: T, updater: CacheUpdater<T>) => void;
  delete: (item: T) => void;
}

export const createInfiniteListOps = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  identify: Identifier<T>
): InfiniteListOps<T> => ({
  ...createBaseOps<InfiniteData<T[]>>(queryClient, queryKey),

  addOrUpdate: (item) => {
    queryClient.setQueryData<InfiniteData<T[]>>(queryKey, (old) => {
      if (!old) return old;
      return produce(old, (draft) => {
        const pages = draft.pages as T[][];
        const pos = findInPages(pages, item, identify);
        if (pos) pages[pos[0]][pos[1]] = item;
        else if (pages[0]) pages[0].unshift(item);
      });
    });
  },

  patch: (item, updater) => {
    queryClient.setQueryData<InfiniteData<T[]>>(queryKey, (old) => {
      if (!old) return old;
      return produce(old, (draft) => {
        const pages = draft.pages as T[][];
        const pos = findInPages(pages, item, identify);
        if (pos) applyDraftUpdate(pages[pos[0]][pos[1]] as object, updater);
      });
    });
  },

  delete: (item) => {
    queryClient.setQueryData<InfiniteData<T[]>>(queryKey, (old) => {
      if (!old) return old;
      return produce(old, (draft) => {
        const pages = draft.pages as T[][];
        const pos = findInPages(pages, item, identify);
        if (pos) pages[pos[0]].splice(pos[1], 1);
      });
    });
  },
});

interface InfinitePaginatedListOps<T> {
  get: () => InfiniteData<PaginatedResponse<T[]>> | undefined;
  replace: (data: InfiniteData<PaginatedResponse<T[]>>) => void;
  invalidate: () => void;
  reset: () => void;
  remove: () => void;
  addOrUpdate: (item: T) => void;
  patch: (item: T, updater: CacheUpdater<T>) => void;
  delete: (item: T) => void;
}

export const createInfinitePaginatedListOps = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  identify: Identifier<T>
): InfinitePaginatedListOps<T> => ({
  ...createBaseOps<InfiniteData<PaginatedResponse<T[]>>>(queryClient, queryKey),

  addOrUpdate: (item) => {
    queryClient.setQueryData<InfiniteData<PaginatedResponse<T[]>>>(
      queryKey,
      (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const pages = draft.pages as PaginatedResponse<T[]>[];
          const pos = findInPaginatedPages(pages, item, identify);
          if (pos) pages[pos[0]].items[pos[1]] = item;
          else if (pages[0]) pages[0].items.unshift(item);
        });
      }
    );
  },

  patch: (item, updater) => {
    queryClient.setQueryData<InfiniteData<PaginatedResponse<T[]>>>(
      queryKey,
      (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const pages = draft.pages as PaginatedResponse<T[]>[];
          const pos = findInPaginatedPages(pages, item, identify);
          if (pos) {
            applyDraftUpdate(pages[pos[0]].items[pos[1]] as object, updater);
          }
        });
      }
    );
  },

  delete: (item) => {
    queryClient.setQueryData<InfiniteData<PaginatedResponse<T[]>>>(
      queryKey,
      (old) => {
        if (!old) return old;
        return produce(old, (draft) => {
          const pages = draft.pages as PaginatedResponse<T[]>[];
          const pos = findInPaginatedPages(pages, item, identify);
          if (pos) pages[pos[0]].items.splice(pos[1], 1);
        });
      }
    );
  },
});
